/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — left sidebar: history list + delete +
   add-timesheet-by-date (Appendix A preserved items).
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

import { Input } from '../../components/ui/input';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { StatusBadge } from '../../components/StatusBadge';
import type { Timesheet } from '../../../shared/types';
import { formatShort, addDays, todayISO, yearOf } from './dateUtils';

export interface TimesheetSidebarProps {
  memberName: string;
  currentWeekStart: string;
  allTimesheets: Timesheet[];
  onSelectWeek: (weekStart: string) => void;
  onDelete: (id: string) => void;
  onAddByDate: (dateISO: string) => void;
}

export function TimesheetSidebar({
  memberName,
  currentWeekStart,
  allTimesheets,
  onSelectWeek,
  onDelete,
  onAddByDate,
}: TimesheetSidebarProps) {
  const [pickedDate, setPickedDate] = useState(todayISO());
  const [pendingDelete, setPendingDelete] = useState<Timesheet | null>(null);

  return (
    <div
      style={{
        width: rem(240),
        flexShrink: 0,
        borderRight: '1px solid var(--bd)',
        background: 'var(--sf)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: `var(--space-4) ${rem(14)} ${rem(10)}`,
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
            marginBottom: 'var(--space-1)',
          }}
        >
          Timesheets
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>{memberName}</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {allTimesheets.length === 0 && (
          <div
            style={{
              padding: `${rem(20)} ${rem(14)}`,
              textAlign: 'center',
              color: 'var(--tm)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            No timesheets yet
          </div>
        )}
        {allTimesheets.map((ts) => {
          const isSelected = ts.weekStart === currentWeekStart;
          return (
            <div
              key={ts.id}
              onClick={() => onSelectWeek(ts.weekStart)}
              style={{
                padding: `var(--space-2) ${rem(14)}`,
                cursor: 'pointer',
                borderBottom: '1px solid var(--bd)',
                background: isSelected ? 'rgba(var(--acr),0.08)' : undefined,
                borderLeft: isSelected ? '3px solid var(--ac)' : '3px solid transparent',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                  {formatShort(ts.weekStart)} — {formatShort(addDays(ts.weekStart, 6))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                  <StatusBadge
                    status={ts.status}
                    style={{ fontSize: 'var(--font-size-xs)', padding: `1px ${rem(6)}` }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(ts);
                    }}
                    title="Delete timesheet"
                    aria-label="Delete timesheet"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--dg)',
                      cursor: 'pointer',
                      padding: 'var(--space-1)',
                      minWidth: 'var(--space-6)',
                      minHeight: 'var(--space-6)',
                      opacity: 0.6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon={Trash2} em={0.9231} />
                  </button>
                </div>
              </div>
              <div
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', marginTop: rem(2) }}
              >
                {yearOf(ts.weekStart)}
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          padding: `${rem(10)} ${rem(14)}`,
          borderTop: '1px solid var(--bd)',
          display: 'flex',
          flexDirection: 'column',
          gap: rem(6),
        }}
      >
        <label
          htmlFor="ts-new-week-picker"
          style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--tm)' }}
        >
          Week starting:
        </label>
        <Input
          id="ts-new-week-picker"
          type="date"
          value={pickedDate}
          onChange={(e) => setPickedDate(e.target.value)}
        />
        <Button onClick={() => onAddByDate(pickedDate)}>+ Add Timesheet</Button>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this timesheet?"
        description="All associated hour entries for this week will be removed."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) onDelete(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
